/**
 * Messenger UI: alternating in/out bubbles, typing indicator, input bar.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { splitItem } from "./_mockHelpers.js";

export const chatBubbleStack: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const sender = inputs.subtext ?? "Allyver";
  const tag = inputs.text ?? "";
  const items = (inputs.items?.length ? inputs.items : ["Hey 👋|in", "Bist du dabei?|out", "Klar — los geht's.|in"]).slice(0, 8);
  const isVertical = ctx.format === "9:16";

  const cardW = isVertical ? 760 : 640;
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);
  const xs = typeSize("xs", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const cardId = `${clipPrefix}-card`;
  const typingId = `${clipPrefix}-typing`;
  const bubbleIds = items.map((_, i) => `${clipPrefix}-b-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const bubbleHtml = items.map((raw, i) => {
    const [text, dir] = splitItem(raw);
    const isOut = (dir ?? "in").toLowerCase().startsWith("o");
    const align = isOut ? "flex-end" : "flex-start";
    const bg = isOut ? accent : withAlpha(themeColors.fg, 0.08);
    const fg = isOut ? themeColors.bg : themeColors.fg;
    const tail = isOut
      ? `border-radius:22px 22px 6px 22px;`
      : `border-radius:22px 22px 22px 6px;`;
    const avatar = !isOut ? `<div style="width:32px;height:32px;min-width:32px;border-radius:9999px;background:${withAlpha(accent, 0.25)};border:1.5px solid ${withAlpha(accent, 0.5)};display:flex;align-items:center;justify-content:center;color:${accent};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:14px;">${escapeHtml(sender.charAt(0).toUpperCase())}</div>` : "";
    return `<div id="${bubbleIds[i]}" style="display:flex;align-items:flex-end;gap:8px;justify-content:${align};">
      ${!isOut ? avatar : ""}
      <div style="${tail}background:${bg};color:${fg};padding:14px 20px;max-width:78%;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.85}px;line-height:1.32;${isOut ? `box-shadow:0 12px 30px ${withAlpha(accent, 0.4)};` : `border:1px solid ${withAlpha(themeColors.fg, 0.1)};`}">${escapeHtml(text ?? "")}</div>
    </div>`;
  }).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:64px;">
    <div id="${cardId}" style="width:${cardW}px;max-height:90%;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.05)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1.5px solid ${withAlpha(themeColors.fg, 0.16)};border-radius:36px;overflow:hidden;box-shadow:0 60px 120px rgba(0,0,0,0.55), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.18)};backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);">
      <div style="display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid ${withAlpha(themeColors.fg, 0.1)};background:${withAlpha(themeColors.fg, 0.03)};">
        <div style="display:flex;align-items:center;gap:12px;">
          <div style="width:40px;height:40px;border-radius:9999px;background:linear-gradient(135deg, ${accent} 0%, ${withAlpha(accent, 0.5)} 100%);display:flex;align-items:center;justify-content:center;color:${themeColors.bg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:18px;">${escapeHtml(sender.charAt(0).toUpperCase())}</div>
          <div style="display:flex;flex-direction:column;">
            <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.55}px;color:${themeColors.fg};">${escapeHtml(sender)}</div>
            <div style="display:flex;align-items:center;gap:6px;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${xs * 0.65}px;color:${withAlpha(themeColors.fg, 0.6)};">
              <span style="width:8px;height:8px;border-radius:9999px;background:#22c55e;box-shadow:0 0 8px #22c55e;"></span>
              <span>Aktiv</span>
            </div>
          </div>
        </div>
        ${tag ? `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${xs * 0.7}px;letter-spacing:2px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.55)};padding:6px 12px;border-radius:9999px;background:${withAlpha(themeColors.fg, 0.05)};border:1px solid ${withAlpha(themeColors.fg, 0.1)};">${escapeHtml(tag)}</div>` : ""}
      </div>
      <div style="display:flex;flex-direction:column;gap:12px;padding:20px 22px;max-height:560px;overflow:hidden;">
        ${bubbleHtml}
        <div id="${typingId}" style="display:flex;align-items:center;gap:8px;align-self:flex-start;">
          <div style="display:flex;gap:4px;background:${withAlpha(themeColors.fg, 0.08)};padding:14px 16px;border-radius:22px 22px 22px 6px;border:1px solid ${withAlpha(themeColors.fg, 0.1)};">
            ${[0, 1, 2].map((i) => `<div class="${clipPrefix}-tdot" style="width:7px;height:7px;border-radius:9999px;background:${withAlpha(themeColors.fg, 0.55)};"></div>`).join("")}
          </div>
        </div>
      </div>
      <div style="display:flex;align-items:center;gap:10px;padding:14px 22px;border-top:1px solid ${withAlpha(themeColors.fg, 0.1)};background:${withAlpha(themeColors.fg, 0.03)};">
        <div style="flex:1;height:40px;border-radius:9999px;background:${withAlpha(themeColors.fg, 0.06)};border:1px solid ${withAlpha(themeColors.fg, 0.1)};display:flex;align-items:center;padding:0 16px;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${xs * 0.85}px;color:${withAlpha(themeColors.fg, 0.45)};">Nachricht schreiben…</div>
        <div style="width:40px;height:40px;border-radius:9999px;background:${accent};display:flex;align-items:center;justify-content:center;box-shadow:0 0 20px ${withAlpha(accent, 0.55)};">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${themeColors.bg}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
        </div>
      </div>
    </div>
  </div>`;

  const init = [
    gsapSetLine(`#${cardId}`, { opacity: 0, y: 30, scale: 0.96 }),
    ...bubbleIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 14 })),
    gsapSetLine(`#${typingId}`, { opacity: 0 }),
  ].join("\n");

  const tweens: string[] = [
    t.to(`#${cardId}`, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "raw:\"power3.out\"" }, 0.0),
  ];
  const stagger = Math.max(0.35, (clipDurationSec - 1.2) / Math.max(items.length, 1));
  items.forEach((_, i) => tweens.push(t.to(`#${bubbleIds[i]}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.4 + i * stagger)));
  tweens.push(t.to(`#${typingId}`, { opacity: 1, duration: 0.35, ease: "raw:\"power1.out\"" }, 0.4 + items.length * stagger));
  tweens.push(`tl.to(${JSON.stringify("." + clipPrefix + "-tdot")},{y:-4,duration:0.35,repeat:-1,yoyo:true,stagger:0.12,ease:"sine.inOut"},${(clipStartSec).toFixed(4)});`);
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
